import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../../design-system/components/Button';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing[4]};
  margin-top: ${spacing[6]};
  flex-wrap: wrap;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PageInfo = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const PageButtons = styled.div`
  display: flex;
  gap: ${spacing[2]};
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
`;

const PageSizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const PageSizeLabel = styled.label`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
`;

const PageSizeSelect = styled.select`
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 6px;
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  showPageInfo?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  showPageSizeSelector = false,
  showPageInfo = true,
}) => {
  const theme = useTheme();
  const { useTranslation } = require('react-i18next');
  const { t } = useTranslation();

  if (totalPages <= 1 && !showPageSizeSelector) {
    return null;
  }

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const getPageNumbers = (): number[] => {
    const pages: number[] = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push(-1); // Ellipsis
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push(-1); // Ellipsis
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push(-1); // Ellipsis
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push(-1); // Ellipsis
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <PaginationContainer>
      {showPageInfo && (
        <PageInfo>
          {t('common.showingResults', {
            start: startItem,
            end: endItem,
            total: totalItems,
          }) || `Showing ${startItem}-${endItem} of ${totalItems}`}
        </PageInfo>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing[4], flexWrap: 'wrap' }}>
        {showPageSizeSelector && onPageSizeChange && (
          <PageSizeSelector>
            <PageSizeLabel>
              {t('common.itemsPerPage')}:
            </PageSizeLabel>
            <PageSizeSelect
              value={pageSize}
              onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </PageSizeSelect>
          </PageSizeSelector>
        )}
        <PageButtons>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            {t('common.previous')}
          </Button>
          {getPageNumbers().map((page, index) => {
            if (page === -1) {
              return (
                <span
                  key={`ellipsis-${index}`}
                  style={{ padding: spacing[2], color: theme.colors.textSecondary }}
                >
                  ...
                </span>
              );
            }
            return (
              <Button
                key={page}
                variant={page === currentPage ? 'primary' : 'outline'}
                size="sm"
                onClick={() => onPageChange(page)}
              >
                {page}
              </Button>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            {t('common.next')}
          </Button>
        </PageButtons>
      </div>
    </PaginationContainer>
  );
};

export default Pagination;
